<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-10 overflow-y-auto"
      @close="close"
    >
      <div
        class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 transition-opacity bg-gray-500/75"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >&#8203;</span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          >
            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                @click="close"
              >
                <span class="sr-only">Close</span>
                <XIcon
                  class="w-6 h-6"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="sm:flex sm:items-start">
              <div
                class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-teal-100 rounded-full sm:mx-0 sm:h-10 sm:w-10"
              >
                <ld-icon
                  icon="PhoneOutgoingIcon"
                  class="w-4 h-4"
                />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle
                  as="h3"
                  class="text-lg font-medium leading-6 text-gray-900"
                >
                  Manual Dial
                </DialogTitle>
                <div
                  v-if="error"
                  class="rounded-md bg-red-50 p-4"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <ld-icon
                        icon="ShieldExclamationIcon"
                        class="h-5 w-5 text-red-400"
                      />
                    </div>
                    <div class="ml-3">
                      <h3 class="text-sm font-medium text-red-800">
                        Error Initiating Call
                      </h3>
                      <div class="mt-2 text-sm text-red-700">
                        <p>
                          {{ error }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mt-2 text-left">
                  <Combobox
                    v-model="selectedEntry"
                    nullable
                  >
                    <div class="relative">
                      <ComboboxInput
                        ref="inputRef"
                        class="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 sm:text-sm"
                        :display-value="displayEntry"
                        placeholder="Number To Dial"
                        autocomplete="off"
                        @input="onSearchInput"
                      />
                      <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <ld-icon
                          class="w-5 h-5 text-gray-400"
                          icon="SearchIcon"
                        />
                      </div>
                      <ComboboxOptions
                        v-if="searchResults.length > 0"
                        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      >
                        <template
                          v-for="(group, groupName) in groupedResults"
                          :key="groupName"
                        >
                          <div v-if="group.length > 0">
                            <div class="px-3 py-1 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                              *[{{ groupName }}]*
                            </div>
                            <ComboboxOption
                              v-for="entry in group"
                              :key="entry.id"
                              v-slot="{ active, selected }"
                              :value="entry"
                              as="template"
                            >
                              <li
                                :class="[
                                  active ? 'text-white bg-teal-600' : 'text-gray-900',
                                  'cursor-pointer select-none relative py-2 pl-3 pr-9',
                                ]"
                              >
                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                  {{ entry.attributes.name }} {{ entry.attributes.type === 'External' ? '(External)' : '' }} - {{ entry.attributes.phoneNumber }}
                                </span>
                              </li>
                            </ComboboxOption>
                          </div>
                        </template>
                      </ComboboxOptions>
                    </div>
                  </Combobox>
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white border border-transparent rounded-md shadow-sm sm:ml-3 sm:w-auto sm:text-sm"
                :class="{
                  'bg-gray-400': !disableCall,
                  'bg-teal-600  hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500':
                    disableCall,
                }"
                :disabled="!disableCall"
                @click="dial"
              >
                Dial
              </button>
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:w-auto sm:text-sm"
                @click="close"
              >
                Cancel
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { defineComponent, computed, nextTick, onMounted, ref } from 'vue'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'
import LDInput from '/@/components/ui/form/LDInput.vue'
import useLetsDialPhone from '/@/services/letsDialPhone/useLetsDialPhone'
import useLetsDialAPI from '/@/services/letsDialAPI/useLetsDialAPI'
import { useAuthStore } from '/@/store/authStore'
import { CallType } from '@letsdial/api/src/lib/types/Call'
import { useCallStore } from '/@/store/callStore'
import { CallStatusTypes } from '@letsdial/phone/src/lib/types'
import useLetsDialSocket from '/@/services/letsDialSocket/useLetsDialSocket'

import usePhoneNumberValidation from '/@/helpers/usePhoneNumberValidation'
import { watch } from 'vue'

const props = withDefaults(defineProps<{ open: boolean }>(), {
  open: () => false,
})
const emit = defineEmits<{ close: [] }>()
const number = ref('')
const selectedEntry = ref<any>(null)
const searchResults = ref<any[]>([])
const auth = useAuthStore()
const callStore = useCallStore()
const { phone } = useLetsDialPhone()
const api = useLetsDialAPI()
const inputRef = ref(null)
const { socket } = useLetsDialSocket()
const error = ref('')
const disableCall = ref(false)

const groupedResults = computed(() => {
  const groups: Record<string, any[]> = {
    'Internal Users': [],
    'Queues': [],
    'Contacts': [],
  }
  searchResults.value.forEach(entry => {
    if (entry.attributes.type === 'Internal') groups['Internal Users'].push(entry)
    else if (entry.attributes.type === 'Queue') groups['Queues'].push(entry)
    else if (entry.attributes.type === 'External') groups['Contacts'].push(entry)
  })
  return groups
})

import { debounce } from 'lodash'
const fetchMatches = debounce(async (query: string) => {
  if (query.length >= 2) {
    try {
      const res = await api.addressBookEntries.list({ filter: { query: query } })
      searchResults.value = res.data
    } catch (e) {
      searchResults.value = []
    }
  } else {
    searchResults.value = []
  }
}, 300)

const onSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  number.value = target.value
  fetchMatches(target.value)
}

const displayEntry = (entry: any) => {
  return entry ? entry.attributes.phoneNumber : number.value
}

watch(selectedEntry, (val) => {
  if (val) {
    number.value = val.attributes.phoneNumber
  }
})

watch(number, () => {
  const { phoneNumberIsValid } = usePhoneNumberValidation(number.value)
  if (!phoneNumberIsValid()) {
    error.value = 'Invalid phone number'
    disableCall.value = false
  } else {
    error.value = ''
    disableCall.value = true
  }
})
// const open = ref(true)
onMounted(() => {
  auth.setAvailabilityStatus('ON_MANUAL_CALL')
  nextTick(() => {
    if (inputRef.value) {
      // @ts-expect-error: Intentional ignore
      inputRef.value.$el?.querySelector('input')?.focus()
    }
  })
})
const close = () => {
  auth.setAvailabilityStatus('IDLE')
  emit('close')
}
const disable = ref(false)
const dial = async () => {
  const { formatPhoneNumber } = usePhoneNumberValidation(number.value)
  const phoneNumber = formatPhoneNumber()
  disable.value = true
  error.value = ''
  if (auth.user?.id && auth.selectedOrganization?.id) {
    try {
      const call = await api.calls.post({
        type: 'calls',
        attributes: {
          number: phoneNumber.number,
          callType: CallType.customCalls,
        },
        relationships: {
          user: { data: { type: 'users', id: auth.user.id } },
          organization: {
            data: {
              type: 'organizations',
              id: auth.selectedOrganization?.id,
            },
          },
        },
      })
      if (call.data) {
        await callStore.setStatus(CallStatusTypes.PRECONNECT)
        await callStore.setCall(call.data)
        emit('close')
        disable.value = false
        setTimeout(() => {
          if (call.data) {
            phone.call(phoneNumber.number, call.data, {
              clid: call.meta.trunk.clid,
              prefix: call.meta.trunk.prefix,
              trunkName: call.meta.trunk.trunk_name,
            })
          }
        }, 500)
      }
    } catch (e) {
      disable.value = false
      // @ts-expect-error: Intentional ignore
      if (e.response && e.response.data) {
        // @ts-expect-error: Intentional ignore
        error.value =
          e.response.data.errors[0]?.detail ?? e.response.data.errors[0]?.title
      } else {
        // @ts-expect-error: Intentional ignore
        error.value = e.message
      }
    }
  }
}
</script>
